/**
 * v3.18-1296 — Unified Communication Notification Center.
 *
 * Extends the existing task notification bell to first-class handle
 * communication events (call / missed_call / voicemail / sms /
 * email). Every notification survives refresh via the persistent
 * `/api/notifications` collection.
 *
 * Features:
 *   • Filter tabs: All / Tasks / Comms
 *   • Audible alert on incoming comms notifications
 *   • Browser Notification API integration
 *   • Intelligent grouping (contact_id + channel dedupes so refresh
 *     does not multiply notifications)
 *   • Quick actions per row: call back, reply SMS, reply email,
 *     open client, dismiss
 *   • Live client local time + quiet-hours indicator when a client
 *     is attached
 */
import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
  Bell, CheckCheck, X, PhoneCall, PhoneMissed, Voicemail,
  MessageSquare, Mail, User, ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';
import { Tabs, TabsList, TabsTrigger } from './ui/tabs';
import api from '../lib/api';
import ClientLocalTime from './ClientLocalTime';
import { dispatchDial, dispatchCompose } from '../lib/comm_bus';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const COMMS_TYPES = new Set([
  'call', 'incoming_call', 'call_inbound',
  'missed_call', 'call_missed',
  'voicemail',
  'sms', 'sms_inbound',
  'email', 'email_inbound',
]);

const isCommsNotification = (n) => {
  const t = (n?.type || n?.channel || '').toLowerCase();
  if (COMMS_TYPES.has(t)) return true;
  // Fallback: emitters that shove channel into `notification_type`.
  const kt = (n?.notification_type || '').toLowerCase();
  return COMMS_TYPES.has(kt);
};

const channelMeta = (n) => {
  const t = (n?.type || n?.channel || n?.notification_type || '').toLowerCase();
  if (t.includes('missed'))     return { icon: PhoneMissed, color: 'text-red-600',    bg: 'bg-red-50',   label: 'Missed call' };
  if (t.includes('voicemail'))  return { icon: Voicemail,   color: 'text-purple-600', bg: 'bg-purple-50', label: 'Voicemail' };
  if (t.includes('call'))       return { icon: PhoneCall,   color: 'text-emerald-600', bg: 'bg-emerald-50', label: 'Call' };
  if (t.includes('sms') || t.includes('text')) return { icon: MessageSquare, color: 'text-blue-600', bg: 'bg-blue-50', label: 'SMS' };
  if (t.includes('email'))      return { icon: Mail,        color: 'text-indigo-600', bg: 'bg-indigo-50', label: 'Email' };
  return null;
};

// Lazily-instantiated soft chime; a short data-URI keeps it self-
// contained without shipping asset files.
let _chime = null;
const playChime = () => {
  try {
    if (!_chime) {
      _chime = new Audio(
        'data:audio/wav;base64,UklGRlwEAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YTgEAAB/AH8AfwB/AH8AfwB/AH8AfwB/AH8AfwB/AH8AfwB/AH8AfwB/AH8AfwB/AH8AfwB/AH8AfwB/AH8AfwB/AH8AfwB/AH8AfwB/AH8AfwB/AH8AfwB/AH8AfwB/AH8AfwB/AH8AfwB/AH8AfwB/AH8AfwB/AH8AfwB/AH8AfwB/AH8AfwB/AA=='
      );
      _chime.volume = 0.4;
    }
    _chime.currentTime = 0;
    void _chime.play();
  } catch (_e) { /* silent */ }
};

const groupKey = (n) => {
  // Duplicates are grouped when arriving on the same channel for
  // the same contact/client within a rolling 5-minute window.
  const meta = channelMeta(n);
  const bucket = meta ? meta.label : (n?.type || 'task');
  const target = n?.contact_id || n?.client_id || n?.link || n?.id;
  return `${bucket}::${target}`;
};

const NotificationCenter = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState('all');
  const [browserPerm, setBrowserPerm] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'default',
  );
  const esRef = useRef(null);
  const initialFetchDone = useRef(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get('/notifications', { params: { limit: 50 } });
      setItems(data?.notifications || []);
    } catch (_e) { /* silent */ }
  }, []);

  useEffect(() => {
    refresh().finally(() => { initialFetchDone.current = true; });
    // v3.21-1729 · Same bad-key fix as PersistentNotificationTray:
    // legacy `localStorage.getItem('token')` returned null under the
    // sessionStorage regime and disabled the SSE fallback stream.
    const token = sessionStorage.getItem('access_token')
      || localStorage.getItem('access_token');
    if (!token || !BACKEND) return undefined;
    const url = `${BACKEND}/api/notifications/stream?token=${encodeURIComponent(token)}`;
    let es;
    try {
      es = new EventSource(url);
      esRef.current = es;
      es.addEventListener('notification', (evt) => {
        try {
          const n = JSON.parse(evt.data);
          const isComm = isCommsNotification(n);
          // Sonner in-page toast (comms get channel icon).
          const meta = channelMeta(n);
          toast(n.title, {
            description: n.body,
            duration: isComm ? 10000 : 8000,
            action: n.link ? {
              label: 'Open',
              onClick: () => { navigate(n.link); markRead(n.id); },
            } : undefined,
            id: n.id,
            className: meta ? meta.bg : undefined,
          });
          // Audible + browser notification only for comms events.
          if (isComm) {
            playChime();
            if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
              try {
                new Notification(n.title || 'Communication', {
                  body: n.body || (meta ? meta.label : ''),
                  tag: groupKey(n),  // dedupe at OS level
                  renotify: false,
                });
              } catch (_ne) { /* silent */ }
            }
          }
          setItems((prev) => {
            // Intelligent grouping: replace an existing row with the
            // same group key rather than appending a duplicate.
            const key = groupKey(n);
            const withoutDupe = prev.filter((x) => groupKey(x) !== key);
            return [n, ...withoutDupe].slice(0, 100);
          });
        } catch (_pe) { /* ignore parse errors */ }
      });
      es.onerror = () => { /* auto-reconnects */ };
    } catch (_eErr) { /* graceful no-op */ }
    return () => { try { es?.close(); } catch (_) { /* noop */ } };
  }, []);

  const requestBrowserPerm = async () => {
    if (typeof Notification === 'undefined') return;
    try {
      const p = await Notification.requestPermission();
      setBrowserPerm(p);
      if (p === 'granted') toast.success('Browser notifications enabled');
    } catch (_e) { /* ignore */ }
  };

  const markRead = async (id) => {
    try { await api.patch(`/notifications/${id}/read`); } catch (_e) { /* ok */ }
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
  };

  const markAllRead = async () => {
    try { await api.patch('/notifications/mark-all-read'); } catch (_e) { /* ok */ }
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  const dismissRow = async (n, e) => {
    e?.stopPropagation();
    await markRead(n.id);
    // Remove from local list immediately.
    setItems((prev) => prev.filter((x) => x.id !== n.id));
  };

  // v3.18-1301 — snooze / remind / reassign quick actions.
  const snoozeRow = async (n, minutes = 15, e) => {
    e?.stopPropagation();
    try {
      await api.post(`/notifications/${n.id}/snooze`, { minutes });
      toast.success(`Snoozed for ${minutes} min`);
      setItems((prev) => prev.filter((x) => x.id !== n.id));
    } catch (_e) { toast.error('Snooze failed'); }
  };
  const remindRow = async (n, minutes = 60, e) => {
    e?.stopPropagation();
    try {
      await api.post(`/notifications/${n.id}/remind`, { minutes });
      toast.success(`Will remind in ${minutes} min`);
    } catch (_e) { toast.error('Remind failed'); }
  };
  const reassignRow = async (n, e) => {
    e?.stopPropagation();
    const target = window.prompt('Reassign to user id (or email):');
    if (!target) return;
    const note = window.prompt('Optional handoff note:') || '';
    try {
      await api.post(`/notifications/${n.id}/reassign`, {
        target_user_id: target.trim(), note,
      });
      toast.success('Notification reassigned');
      setItems((prev) => prev.filter((x) => x.id !== n.id));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Reassign failed');
    }
  };

  const quickCall = (n, e) => {
    e?.stopPropagation();
    if (!n.phone_number) return;
    dispatchDial({ number: n.phone_number, contact_id: n.contact_id, client_id: n.client_id });
    markRead(n.id);
  };
  const quickSms = (n, e) => {
    e?.stopPropagation();
    if (!n.phone_number) return;
    // For now open the DialPad; a future slice can add an SMS composer bus.
    dispatchDial({ number: n.phone_number, contact_id: n.contact_id, client_id: n.client_id });
    markRead(n.id);
  };
  const quickEmail = (n, e) => {
    e?.stopPropagation();
    if (!n.email_address) return;
    dispatchCompose({ to: n.email_address, contact_id: n.contact_id, client_id: n.client_id });
    markRead(n.id);
  };

  const handleClick = (n) => {
    if (!n.read) markRead(n.id);
    if (n.link) { navigate(n.link); setOpen(false); }
  };

  const filteredItems = useMemo(() => {
    if (tab === 'comms')  return items.filter(isCommsNotification);
    if (tab === 'tasks')  return items.filter((n) => !isCommsNotification(n));
    return items;
  }, [items, tab]);

  const unreadTotal = items.filter((n) => !n.read).length;
  const unreadComms = items.filter((n) => !n.read && isCommsNotification(n)).length;

  // Per-row overdue marker for task notifications.
  const dueState = (n) => {
    if (!n?.due_date || n.due_date === 'Not set') return null;
    try {
      const d = new Date(n.due_date);
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const due = new Date(d.getFullYear(), d.getMonth(), d.getDate());
      if (due < today)     return { label: 'OVERDUE',   cls: 'bg-red-100 text-red-700 border-red-200' };
      if (+due === +today) return { label: 'DUE TODAY', cls: 'bg-amber-100 text-amber-700 border-amber-200' };
      return null;
    } catch (_e) { return null; }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="relative" data-testid="notification-bell">
          <Bell className="h-5 w-5" />
          {unreadTotal > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold flex items-center justify-center"
              data-testid="notification-unread-badge"
            >
              {unreadTotal > 99 ? '99+' : unreadTotal}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[420px] p-0" align="end" data-testid="notification-popover">
        <div className="flex items-center justify-between px-3 py-2 border-b">
          <div className="text-sm font-semibold">Notifications</div>
          <div className="flex items-center gap-1">
            {browserPerm === 'default' && (
              <Button
                variant="ghost" size="sm"
                onClick={requestBrowserPerm}
                data-testid="notification-enable-browser"
                className="text-[11px] h-6 px-1.5"
              >
                Enable browser alerts
              </Button>
            )}
            {unreadTotal > 0 && (
              <Button variant="ghost" size="sm" onClick={markAllRead}
                      data-testid="notification-mark-all-read"
                      className="text-[11px] h-6 px-1.5">
                <CheckCheck className="h-3 w-3 mr-1" />Mark all read
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={() => setOpen(false)} className="h-6 w-6 p-0">
              <X className="h-3 w-3" />
            </Button>
          </div>
        </div>
        <div className="px-2 pt-1.5">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="h-8">
              <TabsTrigger value="all" className="text-[11px] h-6" data-testid="notif-tab-all">
                All ({items.length})
              </TabsTrigger>
              <TabsTrigger value="comms" className="text-[11px] h-6" data-testid="notif-tab-comms">
                Comms {unreadComms > 0 && <span className="ml-1 px-1 rounded bg-red-500 text-white text-[10px]">{unreadComms}</span>}
              </TabsTrigger>
              <TabsTrigger value="tasks" className="text-[11px] h-6" data-testid="notif-tab-tasks">
                Tasks
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        <div className="max-h-[480px] overflow-y-auto">
          {filteredItems.length === 0 ? (
            <div className="py-8 text-center text-xs text-slate-400">No notifications.</div>
          ) : (
            filteredItems.map((n) => {
              const isComm = isCommsNotification(n);
              const meta = channelMeta(n);
              const Icon = meta?.icon || Bell;
              const ds = dueState(n);
              return (
                <div
                  key={n.id}
                  onClick={() => handleClick(n)}
                  className={`w-full text-left px-3 py-2 border-b border-slate-100 hover:bg-slate-50 cursor-pointer
                    ${n.read ? 'opacity-60' : ''} ${meta ? meta.bg : ''}`}
                  data-testid={`notification-row-${n.id}`}
                >
                  <div className="flex items-start gap-2">
                    {!n.read && <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-blue-500 flex-shrink-0" />}
                    <Icon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${meta ? meta.color : 'text-slate-500'}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <div className="text-[12px] font-medium text-slate-800 truncate">{n.title}</div>
                        {ds && (
                          <span className={`text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded border ${ds.cls}`}>
                            {ds.label}
                          </span>
                        )}
                        {meta && (
                          <span className={`text-[9px] uppercase px-1 rounded ${meta.color} bg-white/50`}>
                            {meta.label}
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-slate-500 mt-0.5 line-clamp-2">{n.body}</div>
                      <div className="flex items-center gap-2 flex-wrap mt-1">
                        {n.contact_name && (
                          <span className="inline-flex items-center gap-1 text-[10px] text-slate-600">
                            <User className="h-3 w-3" /> {n.contact_name}
                          </span>
                        )}
                        {n.company_name && (
                          <span className="text-[10px] text-slate-500">· {n.company_name}</span>
                        )}
                        {n.assigned_rep_name && (
                          <span className="text-[10px] text-slate-500">· Rep: {n.assigned_rep_name}</span>
                        )}
                        {(n.client_id || n.timezone) && (
                          <ClientLocalTime
                            timezone={n.timezone || undefined}
                            clientId={n.timezone ? undefined : n.client_id}
                            variant="chip"
                          />
                        )}
                      </div>
                      <div className="text-[10px] text-slate-400 mt-0.5">
                        {n.created_at ? new Date(n.created_at).toLocaleString() : ''}
                      </div>
                      {isComm && (
                        <div className="flex items-center gap-1 mt-1.5">
                          {n.phone_number && (
                            <>
                              <button
                                type="button"
                                onClick={(e) => quickCall(n, e)}
                                className="text-[10px] h-6 px-2 rounded bg-emerald-600 text-white hover:bg-emerald-700"
                                data-testid={`notif-quickcall-${n.id}`}
                              >
                                <PhoneCall className="h-3 w-3 inline mr-1" /> Call back
                              </button>
                              <button
                                type="button"
                                onClick={(e) => quickSms(n, e)}
                                className="text-[10px] h-6 px-2 rounded bg-blue-600 text-white hover:bg-blue-700"
                                data-testid={`notif-quicksms-${n.id}`}
                              >
                                <MessageSquare className="h-3 w-3 inline mr-1" /> SMS
                              </button>
                            </>
                          )}
                          {n.email_address && (
                            <button
                              type="button"
                              onClick={(e) => quickEmail(n, e)}
                              className="text-[10px] h-6 px-2 rounded bg-indigo-600 text-white hover:bg-indigo-700"
                              data-testid={`notif-quickemail-${n.id}`}
                            >
                              <Mail className="h-3 w-3 inline mr-1" /> Reply
                            </button>
                          )}
                          {n.link && (
                            <button
                              type="button"
                              onClick={() => { navigate(n.link); markRead(n.id); setOpen(false); }}
                              className="text-[10px] h-6 px-2 rounded bg-slate-700 text-white hover:bg-slate-900"
                            >
                              <ExternalLink className="h-3 w-3 inline mr-1" /> Open
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={(e) => snoozeRow(n, 15, e)}
                            className="text-[10px] h-6 px-2 rounded border border-slate-200 text-slate-500 hover:bg-slate-50"
                            data-testid={`notif-snooze-${n.id}`}
                            title="Snooze 15 min"
                          >
                            Snooze
                          </button>
                          <button
                            type="button"
                            onClick={(e) => remindRow(n, 60, e)}
                            className="text-[10px] h-6 px-2 rounded border border-slate-200 text-slate-500 hover:bg-slate-50"
                            data-testid={`notif-remind-${n.id}`}
                            title="Remind again in 60 min"
                          >
                            Remind
                          </button>
                          <button
                            type="button"
                            onClick={(e) => reassignRow(n, e)}
                            className="text-[10px] h-6 px-2 rounded border border-slate-200 text-slate-500 hover:bg-slate-50"
                            data-testid={`notif-reassign-${n.id}`}
                            title="Assign to another employee"
                          >
                            Reassign
                          </button>
                          <button
                            type="button"
                            onClick={(e) => dismissRow(n, e)}
                            className="text-[10px] h-6 px-2 rounded border border-slate-200 text-slate-500 hover:bg-slate-50 ml-auto"
                            data-testid={`notif-dismiss-${n.id}`}
                          >
                            Dismiss
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationCenter;
